import React from 'react';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/react-hooks';

import { UserProductDiscoveryConversationHistoryItem } from '../../../api/graphql/fragments/productDiscovery';
import { useShouldShowConversationHistory } from '../../../lib/productDiscovery/conversationHistory';
import {
    EnrichedProductDiscoveryConversationMessage,
    MerchantProductOffer,
} from '../../../lib/productDiscovery/conversationMessages';
import { Overlay } from '../common/Overlay';
import { ConversationHistorySection } from '../common/ConversationHistorySection';
import { useViewportSize } from '../../../style/utils';
import { DesktopHeader } from './DesktopHeader';
import { logUserEventUtil } from '../../../lib/events/userEvents';
import { FavoriteMerchantProductOffersTab } from '../common/FavoriteMerchantProductOffersTab';
import { TabType } from '../ProductDiscoveryPage';
import { DesktopExplorationTab } from './DesktopExplorationTab';
import { useUnsavedMerchantProductOffersStillDisplayed } from '../../../lib/productDiscovery/savedMerchantProductOffers';

export function DesktopProductDiscoveryPage({
    conversationId,
    resetConversation,
    messages,
    setMessages,
    messagesListRef,
    inputText,
    setInputText,
    selectedConversationHistoryItemId,
    setSelectedConversationHistoryItemId,
    failedToFetchConversation,
    setFailedToFetchConversation,
    sendMessage,
    isAssistantThinking,
    setIsAssistantThinking,
    lastConversationHistoryItem,
    lastConversationTitle,
    fetchMoreProductSuggestions,
    isFetchingMoreProductSuggestions,
    isFromSerpEmbedding,
    savedMerchantProductOffers,
    setSavedMerchantProductOffers,
    displayedTab,
    setDisplayedTab,
    sentMessageTimestampRef,
    isReadOnlyConversation,
    setIsReadOnlyConversation,
}: {
    conversationId: string;
    resetConversation: ({
        conversationToReopen,
    }: {
        conversationToReopen?: {
            conversationId: string;
            messages: EnrichedProductDiscoveryConversationMessage[];
        };
    }) => void;
    messages: EnrichedProductDiscoveryConversationMessage[] | undefined;
    setMessages: (messages: EnrichedProductDiscoveryConversationMessage[] | undefined) => void;
    messagesListRef: React.RefObject<HTMLDivElement | null>;
    inputText: string;
    setInputText: (value: string) => void;
    selectedConversationHistoryItemId: string | undefined;
    setSelectedConversationHistoryItemId: (value: string | undefined) => void;
    failedToFetchConversation: boolean;
    setFailedToFetchConversation: (value: boolean) => void;
    sendMessage: (suggestedMessage?: string) => void;
    isAssistantThinking: boolean;
    setIsAssistantThinking: (value: boolean) => void;
    lastConversationHistoryItem: UserProductDiscoveryConversationHistoryItem | undefined;
    lastConversationTitle: string | undefined;
    fetchMoreProductSuggestions: (messageId: string, currentQueryScrollOffsets: number[] | undefined) => void;
    isFetchingMoreProductSuggestions: boolean;
    isFromSerpEmbedding: boolean;
    savedMerchantProductOffers: MerchantProductOffer[];
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    displayedTab: TabType;
    setDisplayedTab: (value: TabType) => void;
    sentMessageTimestampRef: React.MutableRefObject<{ messageIndex: number; timestamp: number } | undefined>;
    isReadOnlyConversation: boolean;
    setIsReadOnlyConversation: (value: boolean) => void;
}) {
    const apolloClient = useApolloClient();
    const { height: viewportHeight } = useViewportSize();
    const inputTextRef = React.useRef<HTMLTextAreaElement>(null);
    const { shouldShowConversationHistory, setShouldShowConversationHistory } = useShouldShowConversationHistory();
    const [isInputBarFocused, setIsInputBarFocused] = React.useState(false);
    const [shouldShowInfoTooltip, setShouldShowInfoTooltip] = React.useState(false);
    usePreventWebpageScroll(shouldShowConversationHistory);
    const [, setBottomPositionScrollButton] = React.useState(0);
    const [shouldShowFeedbackSection, setShouldShowFeedbackSection] = React.useState(false);
    const { unsavedMerchantProductOffersStillDisplayed, setUnsavedMerchantProductOffersStillDisplayed } =
        useUnsavedMerchantProductOffersStillDisplayed(displayedTab, setSavedMerchantProductOffers);
    return (
        <>
            <Overlay
                $isVisible={shouldShowConversationHistory || shouldShowFeedbackSection}
                $isHeaderVisible={shouldShowFeedbackSection}
                onClick={() => {
                    if (shouldShowConversationHistory) setShouldShowConversationHistory(false);
                    if (shouldShowFeedbackSection) {
                        setShouldShowFeedbackSection(false);
                        logUserEventUtil(apolloClient, {
                            type: 'closedFeedbackSectionOnJokoAi',
                            payload: { conversationId },
                        });
                    }
                }}
            />
            <MainContainer $viewportHeight={viewportHeight}>
                <ConversationHistorySection
                    {...{
                        conversationId,
                        resetConversation,
                        setMessages,
                        inputTextRef,
                        shouldShowConversationHistory,
                        setShouldShowConversationHistory,
                        selectedConversationHistoryItemId,
                        setSelectedConversationHistoryItemId,
                        setFailedToFetchConversation,
                        setIsAssistantThinking,
                        lastConversationHistoryItem,
                        lastConversationTitle,
                        setIsReadOnlyConversation,
                    }}
                />
                <ContentContainer>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                        <DesktopHeader
                            {...{
                                shouldShowConversationHistory,
                                setShouldShowConversationHistory,
                                setShouldShowInfoTooltip,
                                shouldShowInfoTooltip,
                                displayedTab,
                                setDisplayedTab,
                                setSelectedConversationHistoryItemId,
                            }}
                        />
                        {displayedTab === TabType.favorites ? (
                            <FavoriteMerchantProductOffersTab
                                {...{
                                    savedMerchantProductOffers,
                                    setSavedMerchantProductOffers,
                                    setUnsavedMerchantProductOffersStillDisplayed,
                                    unsavedMerchantProductOffersStillDisplayed,
                                }}
                            />
                        ) : (
                            <DesktopExplorationTab
                                {...{
                                    shouldShowFeedbackSection,
                                    setShouldShowFeedbackSection,
                                    conversationId,
                                    viewportHeight,
                                    failedToFetchConversation,
                                    messages,
                                    inputText,
                                    setInputText,
                                    inputTextRef,
                                    setIsInputBarFocused,
                                    sendMessage,
                                    isInputBarFocused,
                                    isAssistantThinking,
                                    setBottomPositionScrollButton,
                                    messagesListRef,
                                    selectedConversationHistoryItemId,
                                    resetConversation,
                                    fetchMoreProductSuggestions,
                                    isFetchingMoreProductSuggestions,
                                    isFromSerpEmbedding,
                                    savedMerchantProductOffers,
                                    setSavedMerchantProductOffers,
                                    sentMessageTimestampRef,
                                    isReadOnlyConversation,
                                }}
                            />
                        )}
                    </div>
                </ContentContainer>
            </MainContainer>
        </>
    );
}

function usePreventWebpageScroll(shouldShowConversationHistory: boolean) {
    React.useEffect(() => {
        if (shouldShowConversationHistory) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
    }, [shouldShowConversationHistory]);
}

const MainContainer = styled.div<{
    $viewportHeight: number;
}>`
    display: flex;
    flex-direction: row;
    height: ${({ $viewportHeight }) => $viewportHeight}px;
    position: relative;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    align-items: center;
`;
